import { useMemo, useState } from "react";

import type { ThermometerResponse, ThermometerVehicle } from "../contract/types";
import { useBackend } from "../app/BackendProvider";
import { describeError } from "../app/useResource";
import { formatDeviation, formatDuration, positionTypeLabel, shortVehicleNumber } from "../app/format";
import { useConfig, useNetworkId } from "../config/ConfigProvider";
import { useLines } from "../config/LinesProvider";
import { usePolledResource } from "../cockpit/hooks";
import { MetroSchema } from "./MetroSchema";
import { LinePicker, useDefaultLineId } from "./bits";

/**
 * Vue thermomètre (F3.1 / CDC §5.3, redesign P2-23/24). Deux rails d'arrêts
 * **fixes, même ordre sur les deux** : rail du haut = sens Retour (V2), rail du
 * bas = sens Aller (V1). Seul le véhicule bouge — L→R en bas, R→L en haut.
 *
 * Marqueur véhicule = un **carré** (P2-24) :
 *  - fond : vert clair = le retard est absorbé par le battement au terminus ·
 *    vert foncé = repartira en retard · rouge = en avance ;
 *  - contour gauche `[` = couleur du terminus d'origine, contour droit `]` =
 *    couleur du terminus de destination (P2-27, attribuées côté frontend) ;
 *  - point blanc central = dernière course de la sortie (rentre au dépôt) ;
 *  - 2ᵉ contour noir clignotant = une relève est prévue (jamais qui).
 */
export function Thermometer() {
  const backend = useBackend();
  const networkId = useNetworkId();
  const lines = useLines();
  const config = useConfig();
  const [lineId, setLineId] = useState(useDefaultLineId());

  // P2-29 : si la ligne a un schéma de voies (config), sa vue thermomètre **est**
  // ce schéma (image ligne fidèle) plutôt que les deux rails génériques.
  const schema = config.lineSchemas.find((s) => s.lineId === lineId);

  const { data, error, loading } = usePolledResource(
    (signal) => backend.api.getThermometer(networkId, { lineId }, signal),
    [backend, networkId, lineId],
    10_000,
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 14, flexWrap: "wrap" }}>
        <h2 className="ovl">Thermomètre</h2>
        <LinePicker value={lineId} onChange={setLineId} />
      </div>

      {schema ? (
        <MetroSchema lineId={lineId} schema={schema} />
      ) : loading && !data ? (
        <p className="has-text-grey is-size-7">Chargement…</p>
      ) : error && !data ? (
        <p className="has-text-danger is-size-7">{describeError(error)}</p>
      ) : data ? (
        <ThermometerBoard data={data} lineCode={lines.code(lineId)} />
      ) : null}
    </div>
  );
}

function ThermometerBoard({ data, lineCode }: { data: ThermometerResponse; lineCode: string }) {
  const aller = data.directions.find((d) => d.role === "aller") ?? data.directions.find((d) => d.direction === 0);
  const retour = data.directions.find((d) => d.role === "retour") ?? data.directions.find((d) => d.direction === 1);
  // Axe canonique : la séquence du sens aller. Les deux rails s'y calent.
  const axisStops = aller?.stops ?? retour?.stops ?? [];
  const maxIndex = Math.max(1, axisStops.length - 1);

  const terminusColor = useTerminusColors(data);

  if (!aller || !retour) {
    return <p className="has-text-grey is-size-7">Sens aller / retour indisponibles pour cette ligne.</p>;
  }

  // Fraction [0..1] sur l'axe canonique. Aller : suit `stopIndex`. Retour :
  // parcourt l'axe à rebours (part du terminus aller = droite).
  const xOf = (v: ThermometerVehicle): number => {
    const n = Math.max(1, (v.direction === aller.direction ? aller.stops.length : retour.stops.length) - 1);
    const frac = v.stopIndex / n;
    return clamp01(v.direction === aller.direction ? frac : 1 - frac);
  };

  return (
    <>
      <RailLabel text={`${lineCode} · Retour → ${retour.headsign}`} arrow="left" />
      <Rail
        vehicles={data.vehicles.filter((v) => v.direction === retour.direction)}
        xOf={xOf}
        terminusColor={terminusColor}
        originName={retour.stops[0]?.name ?? ""}
        destName={retour.stops.at(-1)?.name ?? ""}
      />

      <StopAxis stops={axisStops} maxIndex={maxIndex} />

      <Rail
        vehicles={data.vehicles.filter((v) => v.direction === aller.direction)}
        xOf={xOf}
        terminusColor={terminusColor}
        originName={aller.stops[0]?.name ?? ""}
        destName={aller.stops.at(-1)?.name ?? ""}
      />
      <RailLabel text={`${lineCode} · Aller → ${aller.headsign}`} arrow="right" />

      <Legend terminusColor={terminusColor} aller={aller} retour={retour} />
    </>
  );
}

function RailLabel({ text, arrow }: { text: string; arrow: "left" | "right" }) {
  return (
    <div className="ovl" style={{ display: "flex", alignItems: "center", gap: 6, margin: "2px 0" }}>
      {arrow === "left" ? "◂" : null}
      {text}
      {arrow === "right" ? "▸" : null}
    </div>
  );
}

function Rail({
  vehicles,
  xOf,
  terminusColor,
  originName,
  destName,
}: {
  vehicles: ThermometerVehicle[];
  xOf: (v: ThermometerVehicle) => number;
  terminusColor: (name: string) => string;
  originName: string;
  destName: string;
}) {
  const rows = useMemo(() => spreadLabels(vehicles.map(xOf)), [vehicles, xOf]);

  return (
    <div
      style={{
        position: "relative",
        height: 58,
        background: "var(--sentinelle-surface)",
        borderRadius: 3,
        border: "1px solid var(--sentinelle-rule)",
      }}
    >
      <div style={{ position: "absolute", left: 8, right: 8, top: 28, height: 2, background: "var(--sentinelle-rule)" }} />
      {vehicles.length === 0 ? (
        <span className="has-text-grey is-size-7" style={{ position: "absolute", left: 10, top: 20 }}>
          Aucun véhicule dans ce sens.
        </span>
      ) : null}
      {vehicles.map((v, i) => (
        <div
          key={v.journeyId}
          style={{
            position: "absolute",
            left: `calc(8px + ${xOf(v) * 100}% - ${xOf(v) * 16}px)`,
            top: 6 + (rows[i] ?? 0) * 15,
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <VehicleSquare v={v} originColor={terminusColor(originName)} destColor={terminusColor(destName)} />
          <span
            className="mono"
            style={{ fontSize: "0.56rem", lineHeight: 1, color: "var(--sentinelle-ink-2)", whiteSpace: "nowrap" }}
          >
            {shortVehicleNumber(v.vehicleDisplayNumber)}
            <span style={{ opacity: 0.7 }}> {formatDeviation(v.deviationSeconds)}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function VehicleSquare({
  v,
  originColor,
  destColor,
}: {
  v: ThermometerVehicle;
  originColor: string;
  destColor: string;
}) {
  const fond = squareFill(v);
  const title =
    `Véhicule ${shortVehicleNumber(v.vehicleDisplayNumber)} · écart ${formatDeviation(v.deviationSeconds)}` +
    ` · ${positionTypeLabel(v.positionType)}` +
    (v.nextBattementSeconds != null ? ` · battement ${formatDuration(v.nextBattementSeconds)}` : "") +
    (v.endOfService ? " · dernière course (retour dépôt)" : "") +
    (v.releve ? " · relève prévue" : "");

  return (
    <span style={{ position: "relative", width: 16, height: 16, display: "inline-block" }} title={title}>
      <span
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: fond.bg,
          border: "3px solid transparent",
          borderLeftColor: originColor,
          borderRightColor: destColor,
          borderRadius: 2,
          boxShadow: "0 1px 2px rgba(0,0,0,.35)",
        }}
      />
      {v.endOfService ? (
        <span
          aria-hidden
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: "#fff",
            boxShadow: "0 0 0 1px rgba(0,0,0,.4)",
            transform: "translate(-50%,-50%)",
          }}
        />
      ) : null}
      {v.releve ? (
        <span
          aria-hidden
          style={{
            position: "absolute",
            inset: -4,
            border: "2px solid #000",
            borderRadius: 3,
            animation: "mae-blink 1s steps(1) infinite",
          }}
        />
      ) : null}
    </span>
  );
}

/** Fond du carré (P2-24) : avance = rouge · retard absorbé par le battement =
 *  vert clair · retard non absorbé = vert foncé · à l'heure = vert clair. */
function squareFill(v: ThermometerVehicle): { bg: string } {
  if (v.deviationSeconds < 0) return { bg: "#dc2626" }; // avance (interdite, §E.3)
  if (v.deviationSeconds >= 60) {
    const absorbed = v.nextBattementSeconds != null && v.deviationSeconds <= v.nextBattementSeconds;
    return { bg: absorbed ? "#86efac" : "#15803d" };
  }
  return { bg: "#86efac" }; // à l'heure
}

function StopAxis({ stops, maxIndex }: { stops: { stopId: string; name: string }[]; maxIndex: number }) {
  return (
    <div style={{ position: "relative", height: 66, margin: "3px 0" }}>
      {stops.map((stop, i) => {
        const left = `calc(8px + ${(i / maxIndex) * 100}% - ${(i / maxIndex) * 16}px)`;
        return (
          <div key={stop.stopId} style={{ position: "absolute", left, top: 0 }}>
            <div style={{ width: 1, height: 7, background: "var(--sentinelle-ink-3)" }} />
            <div
              className="ovl"
              style={{
                transform: "rotate(38deg)",
                transformOrigin: "left top",
                fontSize: "0.52rem",
                whiteSpace: "nowrap",
                marginTop: 2,
              }}
            >
              {stop.name}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Legend({
  terminusColor,
  aller,
  retour,
}: {
  terminusColor: (name: string) => string;
  aller: ThermometerResponse["directions"][number];
  retour: ThermometerResponse["directions"][number];
}) {
  const termini = uniqueTermini(aller, retour);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14, fontSize: "0.68rem" }}>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", color: "var(--sentinelle-muted)" }}>
        <Swatch color="#86efac" label="retard absorbé par le battement / à l'heure" />
        <Swatch color="#15803d" label="repartira en retard" />
        <Swatch color="#dc2626" label="en avance" />
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", border: "1px solid var(--sentinelle-ink-3)" }} />
          dernière course (retour dépôt)
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, border: "2px solid #000", animation: "mae-blink 1s steps(1) infinite" }} />
          relève prévue
        </span>
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", color: "var(--sentinelle-muted)" }}>
        <span className="ovl">Terminus (contour du carré) :</span>
        {termini.map((name) => (
          <Swatch key={name} color={terminusColor(name)} label={name} />
        ))}
      </div>
    </div>
  );
}

function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{ width: 12, height: 12, borderRadius: 2, background: color }} aria-hidden />
      {label}
    </span>
  );
}

// ── Couleurs de terminus (P2-27) — attribuées côté frontend, stables par nom ──

function useTerminusColors(data: ThermometerResponse): (name: string) => string {
  return useMemo(() => {
    const names = new Set<string>();
    for (const d of data.directions) {
      if (d.stops[0]) names.add(d.stops[0].name);
      const last = d.stops.at(-1);
      if (last) names.add(last.name);
    }
    for (const v of data.vehicles) {
      names.add(v.originHeadsign);
      names.add(v.destinationHeadsign);
    }
    const palette = ["#2b6cb0", "#c026d3", "#0d9488", "#ca8a04", "#dc2626", "#4f46e5", "#65a30d", "#db2777"];
    const map = new Map<string, string>();
    [...names]
      .filter(Boolean)
      .sort()
      .forEach((n, i) => map.set(n, palette[i % palette.length]!));
    return (name: string) => map.get(name) ?? "#64748b";
  }, [data]);
}

function uniqueTermini(
  aller: ThermometerResponse["directions"][number],
  retour: ThermometerResponse["directions"][number],
): string[] {
  const s = new Set<string>();
  for (const d of [aller, retour]) {
    if (d.stops[0]) s.add(d.stops[0].name);
    const last = d.stops.at(-1);
    if (last) s.add(last.name);
  }
  return [...s].filter(Boolean).sort();
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

/** Répartit verticalement les carrés trop proches en abscisse (3 rangées). */
function spreadLabels(xs: number[]): number[] {
  const order = xs.map((p, i) => ({ p, i })).sort((a, b) => a.p - b.p);
  const rowByIndex = new Array<number>(xs.length).fill(0);
  let lastP = -1;
  let row = 0;
  for (const { p, i } of order) {
    if (lastP >= 0 && p - lastP < 0.06) row = (row + 1) % 3;
    else row = 0;
    rowByIndex[i] = row;
    lastP = p;
  }
  return rowByIndex;
}
